/**
 * Service-layer wrappers around `GET /v1/golab/datasets/...`.
 *
 * Each function calls `fetchWithAuth` and returns the decoded JSON shape
 * verbatim (no transformation). Errors propagate as `APIErrorException`
 * from `fetchWithAuth` — consumers (the react-query hooks in
 * `../hooks/useDatasets.ts`) handle them.
 */

import { fetchWithAuth } from '../../../../../utils/api';
import type {
  ChannelDataset,
  ChannelRecordsList,
  Collection,
  CollectionList,
  Coverage,
  CreateCollectionPayload,
  ListChannelRecordsParams,
  ListChannelUploadsParams,
  Overview,
  Quality,
  SaveSplitPayload,
  SplitDetail,
  SubmitUploadPayload,
  SubmitUploadResponse,
  UpdateCollectionPayload,
  UploadHistoryList,
} from '../types';

const BASE_PATH = '/v1/golab/datasets';

export interface ListCollectionsParams {
  /** Page size, 1..100 (server-enforced). Defaults server-side to 50. */
  limit?: number;
  /** Number of items to skip, >= 0. Defaults server-side to 0. */
  offset?: number;
}

export const listCollections = async (
  params?: ListCollectionsParams
): Promise<CollectionList> => {
  const searchParams = new URLSearchParams();
  // Use `!= null` so `offset: 0` still gets sent (truthy check would drop it).
  if (params?.limit != null) searchParams.append('limit', String(params.limit));
  if (params?.offset != null) searchParams.append('offset', String(params.offset));
  const query = searchParams.toString();
  return fetchWithAuth(`${BASE_PATH}/collections${query ? `?${query}` : ''}`);
};

export const getCollection = async (id: string): Promise<Collection> =>
  fetchWithAuth(`${BASE_PATH}/collections/${id}`);

export const listChannelDatasets = async (
  collectionId: string
): Promise<ChannelDataset[]> =>
  fetchWithAuth(`${BASE_PATH}/collections/${collectionId}/channel-datasets`);

/**
 * B16-BE: paginated records for a single channel-dataset, filtered to a
 * closed set of statuses. The BE accepts `?status=<comma-list>` (FE joins
 * with commas; do NOT use repeated `?status=&status=`).
 *
 * `statuses` MUST be non-empty — the BE returns 400 on empty. The hook
 * gates the call (`enabled: statuses.length > 0`) rather than the
 * service short-circuiting, so a misuse surfaces as a BE error rather
 * than silent success-with-no-data.
 */
export const listChannelRecords = async (
  channelDatasetId: string,
  params: ListChannelRecordsParams,
): Promise<ChannelRecordsList> => {
  const sp = new URLSearchParams();
  sp.append('status', params.statuses.join(','));
  if (params.limit != null) sp.append('limit', String(params.limit));
  if (params.offset != null) sp.append('offset', String(params.offset));
  return fetchWithAuth(
    `${BASE_PATH}/channel-datasets/${channelDatasetId}/records?${sp.toString()}`,
  );
};

/**
 * UH-FE: paginated upload-history audit entries for one channel
 * dataset. Calls `GET /channel-datasets/{id}/uploads`. The BE applies
 * `limit=20, offset=0` defaults if either is omitted; the FE sends
 * them explicitly via `params` to keep query-key churn minimal.
 */
export const listChannelUploads = async (
  channelDatasetId: string,
  params: ListChannelUploadsParams = {},
): Promise<UploadHistoryList> => {
  const sp = new URLSearchParams();
  // `!= null` so `offset: 0` still gets sent.
  if (params.limit != null) sp.append('limit', String(params.limit));
  if (params.offset != null) sp.append('offset', String(params.offset));
  const qs = sp.toString();
  return fetchWithAuth(
    `${BASE_PATH}/channel-datasets/${channelDatasetId}/uploads${qs ? `?${qs}` : ''}`,
  );
};

export const getCollectionOverview = async (
  collectionId: string
): Promise<Overview> =>
  fetchWithAuth(`${BASE_PATH}/collections/${collectionId}/overview`);

export const getCollectionCoverage = async (
  collectionId: string
): Promise<Coverage> =>
  fetchWithAuth(`${BASE_PATH}/collections/${collectionId}/coverage`);

export const getCollectionQuality = async (
  collectionId: string
): Promise<Quality> =>
  fetchWithAuth(`${BASE_PATH}/collections/${collectionId}/quality`);

/**
 * Persist a new split configuration for a channel dataset. Returns the
 * persisted split alongside any soft warnings the server emitted (see
 * `SplitDetail` in `../types`).
 */
export const saveSplit = async (
  channelDatasetId: string,
  payload: SaveSplitPayload,
): Promise<SplitDetail> =>
  fetchWithAuth(`${BASE_PATH}/channel-datasets/${channelDatasetId}/split`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Multipart CSV ingest (B17-FE). Sends the file bytes + wizard
 * metadata as form fields; the backend (B17-BE) streams the CSV,
 * persists records via `golab_dataset_records`, and returns the
 * authoritative imported/rejected counts.
 *
 * `fetchWithAuth` already detects `FormData` and skips
 * `Content-Type: application/json` so the browser sets
 * `multipart/form-data; boundary=...` correctly (see
 * `src/utils/api.ts:34-37`). Optional fields are appended only when
 * truthy so the wire doesn't carry the literal string `"undefined"`.
 */
export const submitUpload = async (
  payload: SubmitUploadPayload,
): Promise<SubmitUploadResponse> => {
  const form = new FormData();
  form.append('file', payload.file);
  form.append('collectionId', payload.collectionId);
  form.append('channelKey', payload.channelKey);
  form.append('filename', payload.filename);
  // taxonomyVersionId is required end-to-end; the BE rejects requests
  // without it (422).
  form.append('taxonomyVersionId', payload.taxonomyVersionId);
  return fetchWithAuth(`${BASE_PATH}/uploads`, {
    method: 'POST',
    body: form,
  });
};

/**
 * Create a new draft collection. The server pins it to the live version
 * of `payload.taxonomyKey` and returns the full `Collection` shape.
 * 409 → name already exists; 400 → unknown channel, invalid dimension,
 * or no live taxonomy for the chosen key.
 */
export const createCollection = async (
  payload: CreateCollectionPayload,
): Promise<Collection> =>
  fetchWithAuth(`${BASE_PATH}/collections`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

/**
 * Partial update of a collection's safe fields. 409 → name collision;
 * 404 → not found / cross-org. Caller should send only the changed
 * fields (diff against the current `Collection`).
 */
export const updateCollection = async (
  id: string,
  payload: UpdateCollectionPayload,
): Promise<Collection> =>
  fetchWithAuth(`${BASE_PATH}/collections/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(payload),
  });

/** Flip draft → active. 422 if the collection has no uploads yet. */
export const activateCollection = async (id: string): Promise<Collection> =>
  fetchWithAuth(`${BASE_PATH}/collections/${id}/activate`, { method: 'POST' });

/** Flip active → draft. Idempotent on the server. */
export const deactivateCollection = async (id: string): Promise<Collection> =>
  fetchWithAuth(`${BASE_PATH}/collections/${id}/deactivate`, { method: 'POST' });

/** Hard-delete the collection and all its children + upload history. 204 on success. */
export const deleteCollection = async (id: string): Promise<void> =>
  fetchWithAuth(`${BASE_PATH}/collections/${id}`, { method: 'DELETE' });


/**
 * Aggregator export — matches the dual-export convention used by
 * `taxonomyService.ts`. Consumers can import either bare functions
 * (`import { listCollections } from '...'`) or the namespace
 * (`import { datasetsService } from '...'; datasetsService.listCollections(...)`).
 */
export const datasetsService = {
  listCollections,
  getCollection,
  listChannelDatasets,
  listChannelRecords,
  listChannelUploads,
  getCollectionOverview,
  getCollectionCoverage,
  getCollectionQuality,
  saveSplit,
  submitUpload,
  createCollection,
  updateCollection,
  activateCollection,
  deactivateCollection,
  deleteCollection,
};
